import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Plus, RefreshCw, ExternalLink, Trash2, Archive, Eye, Pencil,
  CheckCircle2, AlertTriangle, XCircle, Globe, FileText, ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const STATUS_STYLE = {
  published: "border-skope/50 text-skope",
  draft: "border-ink/25 text-ink/50",
  scheduled: "border-amber-400/60 text-amber-500",
  archived: "border-red-300 text-red-400",
};

const EMPTY_POST = {
  title: "", slug: "", excerpt: "", category: "Cloud Kitchens", tags: "",
  author: "Skope Kitchens", status: "draft",
  featuredImage: { src: "", alt: "" },
  seo: { title: "", metaDescription: "", focusKeyword: "", canonical: "", noindex: false },
  content: [{ type: "p", text: "" }],
};

const BLOCK_TYPES = [
  { v: "p", l: "Paragraph" }, { v: "h2", l: "Heading 2" }, { v: "h3", l: "Heading 3" },
  { v: "li", l: "Bullet" }, { v: "quote", l: "Quote" }, { v: "img", l: "Image" },
];

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls = "w-full bg-white border border-ink/15 focus:border-skope outline-none px-4 py-3 text-sm text-ink transition-colors";

// ------------------------------------------------------------------ editor
const BlogEditor = ({ token, post, onClose, onSaved }) => {
  const [f, setF] = useState(post || EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [published, setPublished] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSeo = (k, v) => setF((p) => ({ ...p, seo: { ...p.seo, [k]: v } }));
  const setBlock = (i, k, v) => setF((p) => ({ ...p, content: p.content.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));

  const checks = [
    { ok: !!f.title.trim(), label: "Title" },
    { ok: !!f.excerpt.trim() || !!f.seo.metaDescription, label: "Excerpt / meta description" },
    { ok: !!f.featuredImage.src, label: "Featured image" },
    { ok: !f.featuredImage.src || !!f.featuredImage.alt, label: "Image alt text" },
    { ok: f.content.some((b) => b.text?.trim() || b.src), label: "Article content" },
    { ok: !f.content.filter((b) => b.type === "h1").length, label: "Single H1 (the title is the H1)" },
    { ok: !!f.seo.title, label: "SEO title" },
    { ok: !!f.seo.metaDescription, label: "Meta description" },
  ];
  const ready = checks.every((c) => c.ok);

  const save = async (status) => {
    setSaving(true);
    setError("");
    const payload = {
      ...f,
      status,
      tags: typeof f.tags === "string" ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : f.tags,
      content: f.content.filter((b) => (b.text && b.text.trim()) || b.src),
    };
    try {
      let saved;
      if (f.id) {
        const { data } = await axios.put(`${API}/admin/blogs/${f.id}`, payload, auth(token));
        saved = data.post;
        if (data.redirectCreated) saved._redirect = data.redirectCreated;
      } else {
        const { data } = await axios.post(`${API}/admin/blogs`, payload, auth(token));
        saved = data.post;
      }
      if (status === "published") {
        // verify the public URL really works before declaring success
        let publicOk = false;
        try {
          const r = await fetch(`${API}/blog/posts/${saved.slug}`);
          publicOk = r.ok;
        } catch { publicOk = false; }
        setPublished({ ...saved, publicOk });
      } else {
        onSaved(saved);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (published) {
    return (
      <div className="border border-ink/10 bg-white p-8" data-testid="publish-success">
        {published.publicOk ? (
          <>
            <div className="flex items-center gap-3 text-skope">
              <CheckCircle2 size={22} />
              <h2 className="font-display font-semibold text-xl text-ink">Blog published successfully</h2>
            </div>
            <div className="mt-6 space-y-2.5 font-mono text-xs">
              <div className="flex gap-3"><span className="text-ink/40 w-32">PUBLIC URL</span><a className="text-skope" href={`/blog/${published.slug}`} target="_blank" rel="noreferrer">/blog/{published.slug}</a></div>
              <div className="flex gap-3"><span className="text-ink/40 w-32">BLOG LISTING</span><span className="text-ink">VISIBLE ✓</span></div>
              <div className="flex gap-3"><span className="text-ink/40 w-32">SITEMAP</span><span className="text-ink">INCLUDED ✓</span></div>
              <div className="flex gap-3"><span className="text-ink/40 w-32">INDEXABILITY</span><span className="text-ink">READY ✓</span></div>
              <div className="flex gap-3"><span className="text-ink/40 w-32">CANONICAL</span><span className="text-ink">VALID ✓</span></div>
              <div className="flex gap-3"><span className="text-ink/40 w-32">GOOGLE INDEXING</span><span className="text-ink/50">UNKNOWN — Google decides indexing; connect Search Console to monitor</span></div>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-3 text-red-400">
            <XCircle size={22} />
            <div>
              <h2 className="font-display font-semibold text-xl text-ink">Publishing error</h2>
              <p className="mt-1 text-sm text-ink/50">The article is saved as published in the CMS but the public URL could not be verified. Check the Blog Health tab.</p>
            </div>
          </div>
        )}
        <button onClick={() => onSaved(published)} className="mt-8 border border-ink/20 px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase text-ink hover:border-skope hover:text-skope transition-colors" data-testid="publish-done">
          Back to Blogs
        </button>
      </div>
    );
  }

  return (
    <div className="border border-ink/10 bg-white p-6 lg:p-8" data-testid="blog-editor">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 hover:text-skope" data-testid="editor-back">
          <ArrowLeft size={14} /> Back
        </button>
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/35">{f.id ? "Edit article" : "New article"}</span>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Field label="Title" testId="field-title">
            <input className={inputCls} value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" placeholder="Article title" />
          </Field>
          <Field label="URL Slug (permanent once published)" testId="field-slug">
            <input className={inputCls} value={f.slug} onChange={(e) => set("slug", e.target.value)} data-testid="editor-slug" placeholder="auto-generated-from-title" />
            {f.status === "published" && (
              <p className="mt-2 text-xs text-amber-500 flex items-center gap-2"><AlertTriangle size={12} /> Changing the slug changes the public URL. A 301 redirect from the old URL is created automatically.</p>
            )}
          </Field>
          <Field label="Excerpt" testId="field-excerpt">
            <textarea className={`${inputCls} h-20`} value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="editor-excerpt" placeholder="1–2 sentence summary shown on the blog page" />
          </Field>

          <div>
            <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">Article Content</span>
            <div className="mt-3 space-y-3" data-testid="editor-blocks">
              {f.content.map((b, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <select value={b.type} onChange={(e) => setBlock(i, "type", e.target.value)}
                    className="border border-ink/15 bg-white px-2 py-3 text-xs text-ink/60 w-28 shrink-0" data-testid={`block-type-${i}`}>
                    {BLOCK_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                  {b.type === "img" ? (
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <input className={inputCls} placeholder="Image URL" value={b.src || ""} onChange={(e) => setBlock(i, "src", e.target.value)} data-testid={`block-src-${i}`} />
                      <input className={inputCls} placeholder="Alt text" value={b.alt || ""} onChange={(e) => setBlock(i, "alt", e.target.value)} data-testid={`block-alt-${i}`} />
                    </div>
                  ) : (
                    <textarea className={`${inputCls} flex-1 ${b.type === "p" ? "h-24" : "h-12"}`} value={b.text || ""} onChange={(e) => setBlock(i, "text", e.target.value)} data-testid={`block-text-${i}`} />
                  )}
                  <button onClick={() => set("content", f.content.filter((_, j) => j !== i))} className="p-3 text-ink/30 hover:text-red-400" data-testid={`block-del-${i}`} aria-label="Remove block">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => set("content", [...f.content, { type: "p", text: "" }])}
              className="mt-3 flex items-center gap-2 border border-ink/15 px-4 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase text-ink/60 hover:border-skope hover:text-skope transition-colors"
              data-testid="add-block">
              <Plus size={13} /> Add block
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <Field label="Category" testId="field-category"><input className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-category" /></Field>
          <Field label="Tags (comma separated)" testId="field-tags"><input className={inputCls} value={f.tags} onChange={(e) => set("tags", e.target.value)} data-testid="editor-tags" /></Field>
          <Field label="Featured image URL" testId="field-image"><input className={inputCls} value={f.featuredImage.src} onChange={(e) => set("featuredImage", { ...f.featuredImage, src: e.target.value })} data-testid="editor-image" /></Field>
          <Field label="Featured image alt text" testId="field-image-alt"><input className={inputCls} value={f.featuredImage.alt} onChange={(e) => set("featuredImage", { ...f.featuredImage, alt: e.target.value })} data-testid="editor-image-alt" /></Field>
          <Field label="SEO Title" testId="field-seo-title"><input className={inputCls} value={f.seo.title} onChange={(e) => setSeo("title", e.target.value)} data-testid="editor-seo-title" /></Field>
          <Field label="Meta Description" testId="field-meta"><textarea className={`${inputCls} h-20`} value={f.seo.metaDescription} onChange={(e) => setSeo("metaDescription", e.target.value)} data-testid="editor-meta" /></Field>
          <Field label="Focus Keyword" testId="field-focus"><input className={inputCls} value={f.seo.focusKeyword} onChange={(e) => setSeo("focusKeyword", e.target.value)} data-testid="editor-focus" /></Field>
          <Field label="Canonical URL (leave empty for default)" testId="field-canonical"><input className={inputCls} value={f.seo.canonical} onChange={(e) => setSeo("canonical", e.target.value)} data-testid="editor-canonical" /></Field>
          <label className="flex items-center gap-3 text-sm text-ink/60">
            <input type="checkbox" checked={!!f.seo.noindex} onChange={(e) => setSeo("noindex", e.target.checked)} data-testid="editor-noindex" />
            Noindex — ask search engines not to index this article
          </label>

          <div className="border border-ink/10 p-4" data-testid="seo-precheck">
            <div className={`font-mono text-[10px] tracking-[0.25em] uppercase ${ready ? "text-skope" : "text-amber-500"}`}>
              {ready ? "Ready to publish" : "Issues found"}
            </div>
            <ul className="mt-3 space-y-1.5">
              {checks.map((c) => (
                <li key={c.label} className="flex items-center gap-2 text-xs text-ink/60">
                  {c.ok ? <CheckCircle2 size={12} className="text-skope" /> : <AlertTriangle size={12} className="text-amber-500" />}
                  {c.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {error && <p className="mt-6 text-sm text-red-400" data-testid="editor-error">{error}</p>}

      <div className="mt-8 flex flex-wrap gap-3">
        <button onClick={() => save("draft")} disabled={saving}
          className="border border-ink/20 px-6 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-ink hover:border-ink transition-colors disabled:opacity-50"
          data-testid="save-draft">
          Save Draft
        </button>
        <button onClick={() => save("published")} disabled={saving || !ready}
          className="bg-skope text-ink px-8 py-3.5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-ink hover:text-white transition-colors disabled:opacity-40"
          data-testid="publish-button">
          {saving ? "Publishing…" : "Publish"}
        </button>
        {f.slug && (
          <a href={`/blog/${f.slug}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 border border-ink/20 px-6 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-ink/60 hover:border-skope hover:text-skope transition-colors"
            data-testid="preview-link">
            <Eye size={13} /> Preview
          </a>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ health
const BlogHealth = ({ token }) => {
  const [h, setH] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/admin/blog-health`, auth(token)).then(({ data }) => setH(data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  if (!h) return <p className="text-ink/40 text-sm">Loading health report…</p>;

  const Row = ({ label, value, warn }) => (
    <div className="flex items-center justify-between border-b border-ink/5 py-3">
      <span className="text-sm text-ink/55">{label}</span>
      <span className={`font-mono text-sm ${warn ? "text-red-400" : "text-ink"}`} data-testid={`health-${label.replace(/\W+/g, "-").toLowerCase()}`}>{value}</span>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="blog-health">
      <div className={`border p-6 ${h.reconciliation === "HEALTHY" ? "border-skope/40 bg-skope/5" : "border-red-400/50 bg-red-50"}`}>
        <div className="flex items-center gap-3">
          {h.reconciliation === "HEALTHY" ? <CheckCircle2 className="text-skope" size={20} /> : <XCircle className="text-red-400" size={20} />}
          <span className="font-display font-semibold text-lg text-ink">Blog count reconciliation: {h.reconciliation}</span>
        </div>
        <p className="mt-2 text-sm text-ink/50">
          Database published: {h.published} · Publicly visible: {h.publiclyVisible} · In sitemap: {h.inSitemap}
          {h.reconciliation !== "HEALTHY" && " — A published blog is missing from the public page or sitemap. Fix this before deploying."}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="border border-ink/10 p-6">
          <div className="font-mono text-[10px] tracking-[0.25em] text-ink/40 uppercase mb-2">Counts</div>
          <Row label="Total blogs" value={h.total} />
          <Row label="Published" value={h.published} />
          <Row label="Drafts" value={h.drafts} />
          <Row label="Scheduled" value={h.scheduled} />
          <Row label="Archived" value={h.archived} />
          <Row label="Publicly accessible" value={h.publiclyVisible} warn={h.publiclyVisible !== h.published} />
          <Row label="In sitemap" value={h.inSitemap} warn={h.sitemapMissing.length > 0} />
          <Row label="Duplicate titles" value={h.duplicateTitles.length} warn={h.duplicateTitles.length > 0} />
          <Row label="SEO issues" value={h.seoIssues.length} warn={h.seoIssues.length > 0} />
        </div>
        <div className="border border-ink/10 p-6">
          <div className="font-mono text-[10px] tracking-[0.25em] text-ink/40 uppercase mb-2">Google connections</div>
          <div className="flex items-center justify-between py-3 border-b border-ink/5">
            <span className="text-sm text-ink/55">Google Search Console</span>
            <span className="font-mono text-xs text-amber-500" data-testid="gsc-status">NOT CONNECTED</span>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-ink/5">
            <span className="text-sm text-ink/55">Google Analytics 4</span>
            <span className="font-mono text-xs text-amber-500" data-testid="ga-status">NOT CONNECTED</span>
          </div>
          <p className="mt-4 text-xs text-ink/40 leading-relaxed">
            Indexing status and performance data (clicks, impressions, CTR, position) appear here once you connect
            Google Search Console and Analytics. We never show invented numbers — until then this reads NOT CONNECTED.
          </p>
        </div>
      </div>

      {h.seoIssues.length > 0 && (
        <div className="border border-amber-300/60 p-6" data-testid="seo-issues-list">
          <div className="font-mono text-[10px] tracking-[0.25em] text-amber-500 uppercase mb-3">Attention required</div>
          <ul className="space-y-2">
            {h.seoIssues.map((i, idx) => (
              <li key={idx} className="text-sm text-ink/60 flex gap-2"><AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" /> /blog/{i.slug} — {i.issue}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ redirects
const Redirects = ({ token }) => {
  const [items, setItems] = useState([]);
  const [oldPath, setOldPath] = useState("");
  const [newPath, setNewPath] = useState("");
  const load = useCallback(() => {
    axios.get(`${API}/admin/redirects`, auth(token)).then(({ data }) => setItems(data.redirects)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!oldPath.startsWith("/") || !newPath.startsWith("/")) return;
    await axios.post(`${API}/admin/redirects`, { oldPath, newPath }, auth(token));
    setOldPath(""); setNewPath(""); load();
  };

  return (
    <div data-testid="redirects-panel">
      <div className="flex flex-col sm:flex-row gap-3">
        <input className={inputCls} placeholder="Old URL — /blog/old-slug" value={oldPath} onChange={(e) => setOldPath(e.target.value)} data-testid="redirect-old" />
        <input className={inputCls} placeholder="New URL — /blog/new-slug" value={newPath} onChange={(e) => setNewPath(e.target.value)} data-testid="redirect-new" />
        <button onClick={add} className="bg-skope text-ink px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase hover:bg-ink hover:text-white transition-colors shrink-0" data-testid="redirect-add">Add 301</button>
      </div>
      <div className="mt-6 border border-ink/10" data-testid="redirect-list">
        {items.length === 0 && <p className="p-6 text-sm text-ink/40">No redirects yet. Slug changes on published articles create one automatically.</p>}
        {items.map((r) => (
          <div key={r.oldPath} className="flex items-center justify-between px-5 py-3.5 border-b border-ink/5 last:border-0">
            <span className="font-mono text-xs text-ink/60">{r.oldPath} <span className="text-skope">→</span> {r.newPath}</span>
            <span className="font-mono text-[10px] text-ink/30">{fmtDate(r.createdAt)} · 301</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ audit log
const AuditLog = ({ token }) => {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/audit-log`, auth(token)).then(({ data }) => setEntries(data.entries)).catch(() => {});
  }, [token]);
  return (
    <div className="border border-ink/10" data-testid="audit-log">
      {entries.length === 0 && <p className="p-6 text-sm text-ink/40">No blog actions recorded yet.</p>}
      {entries.map((e) => (
        <div key={e.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 border-b border-ink/5 last:border-0 text-sm">
          <span className="font-mono text-[10px] text-skope uppercase tracking-[0.15em]">{e.action.replace(/_/g, " ")}</span>
          <span className="text-ink/70">{e.entity}</span>
          {e.detail && <span className="text-ink/40 text-xs">{e.detail}</span>}
          <span className="ml-auto font-mono text-[10px] text-ink/30">{e.user} · {fmtDate(e.at)}</span>
        </div>
      ))}
    </div>
  );
};

// ------------------------------------------------------------------ blog list
const BlogList = ({ token, onNew, onEdit, reloadKey }) => {
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    axios.get(`${API}/admin/blogs`, auth(token)).then(({ data }) => setPosts(data.posts)).catch(() => setError("Could not load blogs."));
  }, [token]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const setStatus = async (p, status) => {
    await axios.post(`${API}/admin/blogs/${p.id}/status`, { status }, auth(token));
    load();
  };

  const del = async (p) => {
    const warn = "Deleting this article may cause an existing Google URL to return 404 and may affect organic search visibility. Consider archiving instead. Delete permanently?";
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API}/admin/blogs/${p.id}`, { ...auth(token), params: { confirm: "SEO_RISK_ACCEPTED" } });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div data-testid="blog-list-admin">
      <div className="flex items-center justify-between mb-5">
        <span className="font-mono text-[10px] tracking-[0.25em] text-ink/40 uppercase">{posts.length} articles in CMS</span>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 text-ink/50 hover:text-skope" aria-label="Refresh" data-testid="blogs-refresh"><RefreshCw size={15} /></button>
          <button onClick={onNew} className="flex items-center gap-2 bg-skope text-ink px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-ink hover:text-white transition-colors" data-testid="new-blog-button">
            <Plus size={13} /> New Blog
          </button>
        </div>
      </div>
      {error && <p className="mb-4 text-sm text-red-400">{error}</p>}
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="border border-ink/10 bg-white p-5 flex flex-wrap items-center gap-4" data-testid={`blog-row-${p.slug}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border ${STATUS_STYLE[p.status] || STATUS_STYLE.draft}`}>{p.status}</span>
                <span className="font-display font-semibold text-ink truncate">{p.title}</span>
                {p.issues?.length > 0 && (
                  <span className="flex items-center gap-1 text-amber-500 text-xs"><AlertTriangle size={12} />{p.issues.length}</span>
                )}
              </div>
              <div className="mt-1.5 font-mono text-[11px] text-ink/35">/blog/{p.slug} · published {fmtDate(p.publishedAt)} · updated {fmtDate(p.updatedAt)}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-ink/40 hover:text-skope" aria-label="View" data-testid={`view-${p.slug}`}><ExternalLink size={15} /></a>
              <button onClick={() => onEdit(p)} className="p-2 text-ink/40 hover:text-skope" aria-label="Edit" data-testid={`edit-${p.slug}`}><Pencil size={15} /></button>
              {p.status === "published" ? (
                <button onClick={() => setStatus(p, "archived")} className="p-2 text-ink/40 hover:text-amber-500" aria-label="Archive" data-testid={`archive-${p.slug}`}><Archive size={15} /></button>
              ) : (
                <button onClick={() => setStatus(p, "published")} className="p-2 text-ink/40 hover:text-skope" aria-label="Publish" data-testid={`publish-${p.slug}`}><Globe size={15} /></button>
              )}
              <button onClick={() => del(p)} className="p-2 text-ink/40 hover:text-red-400" aria-label="Delete" data-testid={`delete-${p.slug}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ main panel
const AdminBlogCMS = ({ token }) => {
  const [tab, setTab] = useState("blogs");
  const [editing, setEditing] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const openEdit = async (p) => {
    const { data } = await axios.get(`${API}/admin/blogs/${p.id}`, auth(token));
    setEditing({ ...data, tags: (data.tags || []).join(", "), featuredImage: data.featuredImage || { src: "", alt: "" } });
    setTab("editor");
  };

  const TABS = [
    { k: "blogs", l: "Blogs", icon: FileText },
    { k: "health", l: "Blog Health", icon: CheckCircle2 },
    { k: "redirects", l: "Redirects", icon: ExternalLink },
    { k: "audit", l: "Audit Log", icon: RefreshCw },
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-8" data-testid="blogcms-tabs">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => { setTab(t.k); if (t.k !== "editor") setEditing(null); }}
            className={`flex items-center gap-2 border px-4 py-2.5 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${tab === t.k || (t.k === "blogs" && tab === "editor") ? "border-skope text-skope bg-skope/10" : "border-ink/15 text-ink/50 hover:text-ink"}`}
            data-testid={`tab-${t.k}`}>
            <t.icon size={13} /> {t.l}
          </button>
        ))}
      </div>

      {tab === "blogs" && <BlogList token={token} reloadKey={reloadKey} onNew={() => { setEditing(null); setTab("editor"); }} onEdit={openEdit} />}
      {tab === "editor" && (
        <BlogEditor
          token={token}
          post={editing}
          onClose={() => setTab("blogs")}
          onSaved={() => { setTab("blogs"); setReloadKey((k) => k + 1); }}
        />
      )}
      {tab === "health" && <BlogHealth token={token} />}
      {tab === "redirects" && <Redirects token={token} />}
      {tab === "audit" && <AuditLog token={token} />}
    </div>
  );
};

export default AdminBlogCMS;
